/**
 * The contact lens prescription dialog.
 *
 * Three steps: lens -> fitting & Rx -> review & issue. The lens the patient wears now is chosen to start
 * with; choosing another moves straight on, and the fitting step converts today's refraction into
 * contact lens powers for that lens (an older refraction is offered, never converted by itself). Hard
 * lenses (RGP, ortho-k, scleral) are made to order, so their powers come from the diagnostic lens and
 * the over-refraction through it instead.
 *
 * A stock lens is one of the practice's contact lenses in inventory, and each eye one of its boxes - a
 * prescription the practice has there, in stock or (most often) ordered in for the patient. Today's
 * refraction picks the box where the lens has it, and puts the lenses that have it first; a box the
 * practice hasn't is added to the lens in Inventory before it can be prescribed.
 *
 * Each eye can wear its own stock lens (a toric right and a spherical left, a multifocal's D and N
 * lenses), and either eye can have none. Each lens's manufacturer and material are on the prescription,
 * as the Optometry Board of Australia's guideline asks - from inventory, or entered here where the
 * inventory lens has none.
 *
 * The choice of lens and its powers live here, not in dispensing: a contact lens job is made from an
 * issued prescription and only decides the supply, care, delivery and billing - and a made-to-order
 * lens's price. Shares the dispensing dialogs' shell and styling, and saves through LensPrescribingStore.
 *
 * A trial pair that goes home first has its own dialog (lensRxTrialModalController). This one saves the
 * draft and closes with { next: "trial" }, and the prescriptions tile opens the trial; the trial hands
 * back with the step to open at (`startStep`). Until the review finds a good fit, it can't be issued.
 *
 * Only a draft is opened here. An issued prescription is locked - it isn't opened again - and one found
 * to be wrong is withdrawn from the prescriptions tile.
 */
app.controller("lensRxContactsModalController", [
	"$scope",
	"$mdDialog",
	"$timeout",
	"$animate",
	"$element",
	"$filter",
	"$q",
	"LensPrescribingStore",
	"LensDispensingStore",
	"PatientVisionAPI",
	"CurrentPatient",
	"Session",
	"toast",
	"existingRx",
	"startStep",
	function ($scope, $mdDialog, $timeout, $animate, $element, $filter, $q, Rx, Store, PatientVisionAPI, CurrentPatient, Session, toast, existingRx, startStep) {
		$scope.R = Rx;
		$scope.S = Store;

		// See the dispensing dialogs: steps switch instantly and fade themselves in, without ngAnimate.
		$animate.enabled(angular.element($element[0].querySelector(".lens-shell")), false);

		const patient = CurrentPatient.patient || {};
		$scope.patient = patient;
		$scope.patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Patient";
		$scope.initials = ((patient.firstName || "P").charAt(0) + (patient.lastName || "").charAt(0)).toUpperCase();

		$scope.steps = [
			{ key: "lens", label: "Contact lens", icon: "fa-circle-o" },
			{ key: "fitting", label: "Fitting & Rx", icon: "fa-eye" },
			{ key: "issue", label: "Review & issue", icon: "fa-check-square-o" },
		];
		const FITTING = 1;
		const ISSUE = 2;

		// A renewal arrives as an unsaved draft, so it is new though it comes filled in.
		const isNew = !existingRx || !existingRx.id;
		const isRenewal = !!existingRx && !existingRx.id;
		$scope.p = existingRx ? angular.copy(existingRx) : Rx.emptyContactsRx();
		const p = $scope.p;

		$scope.locked = !Rx.isDraft(p);
		// Whoever issues it is the prescriber; until then that's the user writing it.
		if (!$scope.locked) p.cl.prescriber = Rx.myName();
		// A draft waiting on its trial is good for the full term from when it is issued.
		Rx.restartExpiry(p);
		$scope.myRegistration = Rx.myRegistration();
		$scope.visited = { lens: true };
		$scope.dirty = false;

		// ------------------------------------------------------------------
		// Navigation
		// ------------------------------------------------------------------
		// Back from the trial at the step its outcome needs: the review to issue, the fitting to change the
		// powers, the lens to choose another.
		const handedTo = $scope.steps.findIndex((s) => s.key === startStep);
		$scope.current = handedTo !== -1 ? handedTo : isRenewal ? FITTING : existingRx && ($scope.locked || p.product) ? ISSUE : 0;
		if ($scope.current >= FITTING) $scope.steps.slice(0, $scope.current + 1).forEach((s) => ($scope.visited[s.key] = true));

		$scope.go = function (i) {
			if (i < 0 || i >= $scope.steps.length) return;
			$scope.current = i;
			$scope.visited[$scope.steps[i].key] = true;
			// Arriving at the fitting with no powers yet: start from today's refraction, converted.
			if ($scope.steps[i].key === "fitting" && !$scope.locked && p.product && !p.product.custom && $scope.vision.today && !anyPowers()) {
				$scope.convertFromRefraction();
			}
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};
		$scope.next = () => $scope.go($scope.current + 1);
		$scope.back = () => $scope.go($scope.current - 1);

		let advanceTimer = null;
		function autoAdvance() {
			$timeout.cancel(advanceTimer);
			advanceTimer = $timeout(() => $scope.next(), 380);
		}

		$scope.stepDone = function (key) {
			switch (key) {
				case "lens": return $scope.lensChosen();
				case "fitting": return $scope.clValid() && fitSettled();
				case "issue": return !Rx.isDraft(p);
			}
			return false;
		};

		$scope.progress = function () {
			const done = $scope.steps.filter((s) => $scope.stepDone(s.key)).length;
			return Math.round((done / $scope.steps.length) * 100);
		};

		$scope.stepSummary = function (key) {
			switch (key) {
				case "lens":
					if ($scope.isMadeToOrder()) return $scope.lensChosen() ? `${p.product.name} · made to order` : "Made to order - describe it";
					return p.product ? Rx.title(p) : "Choose a contact lens";
				case "fitting": return $scope.clValid() ? `R ${eyeShort("od")}  L ${eyeShort("os")}${p.fitting.trialIssued ? (fitSettled() ? " · trialled" : " · on trial") : ""}` : "Fit and prescription";
				case "issue":
					if (Rx.isWithdrawn(p)) return `Withdrawn ${$filter("date")(p.withdrawn && p.withdrawn.at, "d MMM yyyy")}`;
					return Rx.isIssued(p) ? `Issued ${$filter("date")(p.issuedAt, "d MMM yyyy")}` : "Check & issue";
			}
		};
		const eyeShort = (k) => ($scope.noLens(k) ? "none" : Store.fmtPower(p.cl[k].sph));

		$scope.status = () => Rx.statusOf(patient.id, p);
		$scope.statusLabel = () => (Rx.isDraft(p) ? (p.id ? "Draft" : "New") : $scope.status().label);

		$scope.$watch("p", (n, o) => {
			if (n !== o) $scope.dirty = true;
		}, true);

		// ------------------------------------------------------------------
		// Each eye's lens
		// ------------------------------------------------------------------
		$scope.eyes = [{ key: "od", label: "Right", sub: "OD" }, { key: "os", label: "Left", sub: "OS" }];
		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);
		function num(v) {
			return isNum(v) ? Number(v) : null;
		}

		$scope.lens = (eye) => Store.lensFor(p, eye);
		$scope.noLens = (eye) => !!p.cl[eye].noLens;
		const lensEyes = () => Store.lensEyes(p);
		const anyPowers = () => ["od", "os"].some((k) => isNum(p.cl[k].sph));

		$scope.isCustom = () => !!p.product && !!p.product.custom;
		$scope.isToric = (eye) => !!$scope.lens(eye || "od") && $scope.lens(eye || "od").design === "toric";
		$scope.isMultifocal = (eye) => !!$scope.lens(eye || "od") && $scope.lens(eye || "od").design === "multifocal";
		$scope.isFromInventory = (eye) => !!$scope.lens(eye || "od") && !!$scope.lens(eye || "od").fromInventory;
		// An inventory lens whose boxes hold their prescriptions: the eye is one of those boxes.
		$scope.hasBoxes = (eye) => !!$scope.lens(eye) && !!$scope.lens(eye).boxes;

		// The boxes an eye's lens comes in, strongest plus first, each with what's on hand - the same list
		// while the lens is the same, for the dropdown.
		const boxLists = {};
		$scope.boxes = function (eye) {
			const c = $scope.lens(eye);
			if (!c || !c.boxes) return [];
			const key = `${c.id}:${c.variations.length}`;
			if (!boxLists[key]) {
				boxLists[key] = c.variations.filter((v) => isNum(v.rx.sph))
					.sort((a, b) => b.rx.sph - a.rx.sph || (b.rx.cyl || 0) - (a.rx.cyl || 0) || (a.rx.axis || 0) - (b.rx.axis || 0))
					.map((v) => ({ id: v.variationId, v: v, label: `${Store.clRxText(v.rx)}  —  ${v.stock ? v.stock + " in stock" : "order in"}` }));
			}
			return boxLists[key];
		};

		// Each eye's box, as its prescription names it.
		$scope.box = { od: null, os: null };
		$scope.$watch(
			() => ["od", "os"].map((k) => ($scope.hasBoxes(k) && Store.stockFor($scope.lens(k), p.cl[k]) || {}).variationId || "").join("|"),
			function (ids) {
				const parts = ids.split("|");
				$scope.box.od = parts[0] || null;
				$scope.box.os = parts[1] || null;
			}
		);
		$scope.applyBox = function (k) {
			if ($scope.locked) return;
			const chosen = $scope.boxes(k).find((b) => b.id === $scope.box[k]);
			angular.extend(p.cl[k], chosen ? chosen.v.rx : NO_BOX);
		};

		// The powers an eye of an inventory lens needs that the lens has no box in - from the refraction, a
		// trial's review, or another lens - to add in Inventory before it can be prescribed; "" when the eye
		// is one of its boxes.
		$scope.missingBox = function (k) {
			const e = p.cl[k];
			if (!$scope.hasBoxes(k) || e.noLens || !isNum(e.sph) || Store.stockFor($scope.lens(k), e)) return "";
			return Store.clRxText({ sph: e.sph, cyl: e.cyl, axis: e.axis, add: e.add });
		};

		// The lenses on the prescription: one for both eyes, or the right's and the left's. The same list
		// while the lenses are the same - the template repeats over it, and a fresh list every digest never
		// settles.
		let slotsKept = { product: undefined, productOs: undefined, list: [] };
		$scope.lensSlots = function () {
			if (slotsKept.product === p.product && slotsKept.productOs === p.productOs) return slotsKept.list;
			let list = [];
			if (p.product && !p.productOs) list = [{ eye: "od", label: "Lens", lens: p.product }];
			if (p.product && p.productOs) list = [{ eye: "od", label: "Right lens", lens: p.product }, { eye: "os", label: "Left lens", lens: p.productOs }];
			slotsKept = { product: p.product, productOs: p.productOs, list: list };
			return list;
		};

		// ------------------------------------------------------------------
		// Latest refraction - drives the suggested lens and the converted powers
		// ------------------------------------------------------------------
		$scope.vision = { loading: true, data: null, date: null, today: false, comments: [] };

		// The comment written with the refraction, shown beside it when it is offered. The response
		// carries each eye's comments with who and when; the newest one is the refraction's.
		function latestRefractionComment(eye) {
			const list = ((eye && eye.refraction_comments) || []).filter((c) => c && c.comment);
			if (!list.length) return null;
			const newest = list.reduce((a, b) => (new Date(a.date_time || 0) >= new Date(b.date_time || 0) ? a : b));
			return newest.comment;
		}
		function refractionComments(v) {
			const od = latestRefractionComment(v && v.od);
			const os = latestRefractionComment(v && v.os);
			if (od && od === os) return [{ eye: "", text: od }];
			return [od ? { eye: "R", text: od } : null, os ? { eye: "L", text: os } : null].filter(Boolean);
		}

		// The spectacle Rx the contact lenses are converted from, per eye.
		$scope.spectacleRx = function (eye) {
			const v = $scope.vision.data && $scope.vision.data[eye];
			if (!v) return null;
			return { sph: num(v.refraction_sphere), cyl: num(v.refraction_cylinder), axis: num(v.refraction_axis), add: num(v.refraction_add) };
		};

		$scope.fmtSpecs = function (eye) {
			const r = $scope.spectacleRx(eye);
			if (!r || !isNum(r.sph)) return "-";
			let s = Store.fmtPower(r.sph);
			if (isNum(r.cyl) && r.cyl !== 0) s += ` / ${Store.fmtPower(r.cyl)} × ${r.axis || "?"}`;
			if (r.add > 0) s += `  Add ${Store.fmtPower(r.add)}`;
			return s;
		};

		function hasRefraction(v) {
			return v && ["od", "os"].some((k) => v[k] && isNum(v[k].refraction_sphere));
		}

		if (!patient.id) $scope.vision.loading = false;
		else PatientVisionAPI.getPatientVision(patient.id).then(
			function (resp) {
				const v = resp.data && resp.data.vision;
				$scope.vision.loading = false;
				if (hasRefraction(v)) {
					$scope.vision.data = v;
					$scope.vision.comments = refractionComments(v);
					const audit = v.od_vision_audit && v.od_vision_audit.refraction_sphere;
					$scope.vision.date = audit ? new Date(audit) : null;
					$scope.vision.today = !!$scope.vision.date && moment($scope.vision.date).isSame(moment(), "day");
					// The lenses may have arrived first.
					matchLenses();
					// The lens was picked before the refraction arrived.
					if (isNew && !isRenewal && p.product && !p.product.custom && $scope.vision.today && !anyPowers()) {
						$scope.convertFromRefraction();
						$timeout(() => ($scope.dirty = false));
					}
				}
			},
			function () {
				$scope.vision.loading = false;
			}
		);

		// ------------------------------------------------------------------
		// Step 1 - contact lens
		// ------------------------------------------------------------------
		$scope.lensFilter = { modality: "All", design: "All", text: "" };

		// The practice's lenses from inventory - read afresh, so a box added there since shows - and the
		// catalogue's made-to-order ones. Inventory that can't be read leaves made to order. The patient's
		// jobs are read for the lens they wear.
		$scope.catalogueState = { loading: true, error: null, stockError: false };
		const jobsRead = Session.hasPermission("CLINICAL_LENS_DISPENSE") && !Store.getJobs(patient.id).length && patient.id
			? Store.loadPatientJobs(patient.id).catch(angular.noop)
			: $q.resolve();
		$q.all([
			Store.loadCatalogue().then(() => Store.loadContactLensStock(true).catch(() => ($scope.catalogueState.stockError = true))),
			jobsRead,
		])
			.then(function () {
				matchLenses();
				refreshInventoryLenses();
				startFromWornLens();
			})
			.catch((resp) => ($scope.catalogueState.error = Store.errorMessage(resp, "The lens catalogue could not be loaded.")))
			.finally(() => ($scope.catalogueState.loading = false));
		$scope.inventoryLenses = () => Store.contactLensChoices().some((c) => c.fromInventory);

		// Astigmatism of 0.75D or more wants a toric, an add a multifocal. From the refraction, or the
		// lens Rx already written when there is none.
		$scope.suggestedDesign = function () {
			const eyes = ["od", "os"].map((k) => $scope.spectacleRx(k) || p.cl[k]);
			if (eyes.some((e) => e && Number(e.add) > 0 && !isNaN(e.add))) return "multifocal";
			if (eyes.some((e) => e && Math.abs(Number(e.cyl) || 0) >= 0.75)) return "toric";
			return "spherical";
		};

		// What the refraction asks of each inventory lens of the suggested design: the box each eye's
		// refraction converts to, where the lens has it. Worked out once the lenses and the refraction are
		// both in, for the tiles' badges and order. { lensId: { all, stock } } - `all` when every refracted
		// eye has its box, `stock` the fewest boxes on hand of them.
		$scope.matches = {};
		function matchLenses() {
			const out = {};
			const eyes = ["od", "os"].filter((k) => $scope.spectacleRx(k) && isNum($scope.spectacleRx(k).sph));
			if (eyes.length) {
				Store.contactLensChoices().filter((c) => c.boxes && c.design === $scope.suggestedDesign()).forEach(function (c) {
					const found = eyes.map((k) => Store.boxFor(c, Store.toContactRx($scope.spectacleRx(k), c))).filter(Boolean);
					if (found.length) out[c.id] = { all: found.length === eyes.length, stock: Math.min.apply(null, found.map((v) => v.stock)) };
				});
			}
			$scope.matches = out;
		}
		$scope.match = (c) => $scope.matches[c.id] || null;

		// Lenses with the patient's prescription first: both eyes', then one eye's, then the rest.
		const matchRank = (c) => ($scope.matches[c.id] ? ($scope.matches[c.id].all ? 0 : 1) : 2);
		$scope.visibleLenses = function () {
			const f = $scope.lensFilter;
			const text = (f.text || "").toLowerCase();
			return Store.contactLensChoices().filter((c) =>
				(f.modality === "All" || c.modality === f.modality) &&
				(f.design === "All" || c.design === f.design) &&
				(!text || `${c.brand} ${c.name} ${c.maker}`.toLowerCase().indexOf(text) !== -1)
			).sort((a, b) => matchRank(a) - matchRank(b));
		};

		// The lens (or lenses) the patient wears now, from their last contact lens prescription or supply.
		$scope.wearing = () => Rx.currentLens(patient.id);
		// A boolean for ng-if: wearing() is a new object on every digest.
		$scope.hasWorn = () => !!$scope.wearing();
		$scope.isWorn = function (c) {
			const w = $scope.wearing();
			return !!w && ((w.product && w.product.id === c.id) || (w.productOs && w.productOs.id === c.id));
		};

		// The worn lens as it is offered now - its parameters from the catalogue or inventory, the
		// prescriber's own words (maker, material, type) kept from the prescription.
		function asOfferedNow(product) {
			if (!product) return null;
			const offered = Store.contactLensChoices().find((c) => c.id === product.id);
			if (!offered) return angular.copy(product);
			const kept = {};
			["brand", "maker", "material", "design", "modality"].forEach((f) => {
				if (product[f]) kept[f] = product[f];
			});
			return angular.extend(angular.copy(offered), kept);
		}

		// A draft or renewal naming an inventory lens takes the lens as inventory has it now - its boxes and
		// what's on hand - keeping the prescriber's own words.
		function refreshInventoryLenses() {
			if ($scope.locked) return;
			const wasDirty = $scope.dirty;
			if (p.product && p.product.fromInventory) p.product = asOfferedNow(p.product);
			if (p.productOs && p.productOs.fromInventory) p.productOs = asOfferedNow(p.productOs);
			if (!wasDirty) $timeout(() => ($scope.dirty = false));
		}

		// A new prescription starts on the lens the patient wears now - the usual case - without moving on.
		function startFromWornLens() {
			if (!isNew || isRenewal || p.product || $scope.locked) return;
			const worn = $scope.wearing();
			// Only a lens still offered - in inventory - is started on; an older catalogue lens isn't.
			if (!worn || !worn.product || !Store.contactLensChoices().some((c) => c.id === worn.product.id)) return;
			p.product = asOfferedNow(worn.product);
			p.productOs = asOfferedNow(worn.productOs);
			fitAllToLenses(false);
			$timeout(() => ($scope.dirty = false));
		}

		$scope.designLabel = (key) => (Store.CL_DESIGNS.find((d) => d.key === key) || {}).label || key;
		$scope.modalityLabel = (key) => Store.modalityMeta(key).label;

		// Cost per eye per month, for comparing a daily against a monthly fairly.
		$scope.monthlyCost = function (c) {
			return (c.price / (c.pack * Store.modalityMeta(c.modality).days)) * 30;
		};

		$scope.selectProduct = function (product) {
			if ($scope.locked) return;
			const changed = !p.product || p.product.id !== product.id;
			const wasCustom = !!p.product && !!p.product.custom;
			// The lens already chosen is kept as it is, with what was entered for it - an inventory lens's
			// manufacturer, material, type and replacement.
			if (changed) {
				p.product = angular.copy(product);
				// A made-to-order lens is specified per eye, so it is one lens for both - as is the left eye's
				// lens chosen here.
				if (product.custom || (p.productOs && p.productOs.id === product.id)) p.productOs = null;
				if (product.custom) fitCustomToProduct(wasCustom);
				else fitAllToLenses(wasCustom);
			}
			// A made-to-order lens is described first, as the patient's own frame is, so it stays here.
			if (!product.madeToOrder) autoAdvance();
		};

		// A lens the lab makes for this patient, not one from the catalogue: its lab, name and design are
		// the prescriber's own words. Dispensing prices it.
		$scope.isMadeToOrder = () => !!p.product && !!p.product.madeToOrder;
		$scope.useMadeToOrder = function () {
			if ($scope.locked || $scope.isMadeToOrder()) return;
			$scope.selectProduct(Store.madeToOrderLens());
		};

		// Chosen, and - when made to order - described well enough for the lab to make it.
		$scope.lensChosen = function () {
			if (!p.product) return false;
			if (!p.product.madeToOrder) return true;
			return !!String(p.product.name || "").trim() && !!String(p.product.maker || "").trim();
		};

		// The lab is also the lens's brand, which is how lenses are named elsewhere.
		$scope.onMadeToOrderLab = function () {
			p.product.brand = p.product.maker;
		};

		// After choosing a custom lens: keep what was specified where this design can take it, and
		// start everything else from the lab's defaults.
		function fitCustomToProduct(wasCustom) {
			const c = p.product;
			const inRange = (v, r) => v !== null && v !== undefined && v >= r[0] && v <= r[1];
			["od", "os"].forEach(function (k) {
				const prev = wasCustom ? p.cl[k] : {};
				const e = Store.customEye(c);
				if (inRange(prev.bc, c.bcRange)) e.bc = prev.bc;
				if (inRange(prev.dia, c.diaRange)) e.dia = prev.dia;
				if (isNum(prev.sph) && inRange(prev.sph, c.range)) e.sph = prev.sph;
				// The type is free text: whatever was written stays.
				if (prev.material) e.material = prev.material;
				if (c.colours.indexOf(prev.colour) !== -1) e.colour = prev.colour;
				if (c.design === "toric") angular.extend(e, { cyl: prev.cyl || null, axis: prev.axis || null });
				if (c.design === "multifocal") e.add = prev.add || null;
				if (prev.trial) e.trial = prev.trial;
				if (isNum(prev.or)) angular.extend(e, { or: prev.or, orCyl: prev.orCyl, orAxis: prev.orAxis });
				if (c.design === "toric" && prev.bcSteep && inRange(prev.bcSteep, c.bcRange)) e.bcSteep = prev.bcSteep;
				e.noLens = !!p.cl[k].noLens;
				p.cl[k] = e;
			});
			if (!wasCustom) p.cl.source = null;
		}

		// After a lens change: convert again if the powers came from the refraction (or there are
		// none yet), otherwise keep the entered powers and only drop what each eye's lens can't take.
		function fitAllToLenses(wasCustom) {
			// Coming from a custom lens, those parameters mean nothing to a stock lens - start again.
			if (wasCustom) {
				["od", "os"].forEach((k) => (p.cl[k] = angular.extend(Store.emptyClRx()[k], { noLens: !!p.cl[k].noLens })));
				p.cl.source = null;
			}
			const fromRefraction = !p.cl.source || p.cl.source === "Converted from refraction";
			if (fromRefraction && $scope.vision.today) return $scope.convertFromRefraction();
			["od", "os"].forEach(fitEyeToLens);
		}

		function fitEyeToLens(k) {
			const c = $scope.lens(k);
			const e = p.cl[k];
			if (!c || e.noLens) return;
			// Only a box the practice has: this one if the lens has it, else the box with these powers - or
			// none yet, the eye keeping the powers it needs to say which box is missing.
			if (c.boxes) {
				const box = Store.stockFor(c, e) || Store.boxFor(c, e);
				angular.extend(e, box ? box.rx : { bc: null, dia: null });
				return;
			}
			if (c.fromInventory) {
				if (c.design !== "toric") angular.extend(e, { cyl: null, axis: null });
				if (c.design !== "multifocal") e.add = null;
				return;
			}
			if (c.bcs.indexOf(e.bc) === -1) e.bc = c.bcs[0];
			e.dia = c.dia;
			if (c.design !== "toric") {
				if (isNum(e.cyl) && e.cyl !== 0 && isNum(e.sph)) e.sph = Store.snapClPower(Number(e.sph) + Number(e.cyl) / 2);
				e.cyl = null;
				e.axis = null;
			} else if ((c.cyls || []).indexOf(e.cyl) === -1) {
				e.cyl = null;
			}
			if (c.design !== "multifocal" || (c.adds || []).indexOf(e.add) === -1) e.add = null;
		}

		// ------------------------------------------------------------------
		// Step 2 - fitting & Rx
		// ------------------------------------------------------------------
		$scope.converted = { od: false, os: false };

		// Each eye converted for its own lens; a plus-cylinder refraction is transposed on the way. A
		// made-to-order lens takes the refraction only as a starting point - its powers, not its fit -
		// for the diagnostic lens and over-refraction to confirm, and only when the fitter asks.
		$scope.convertFromRefraction = function () {
			if ($scope.locked || !p.product) return;
			if (p.product.custom) return startFromRefraction();
			lensEyes().forEach(convertEye);
			p.cl.source = "Converted from refraction";
		};

		// One eye's powers from the refraction, for its lens. A base curve already chosen stays while the
		// lens is made in it. An inventory lens is prescribed in the boxes the practice has: the one with
		// these powers - or, when it has none, the powers alone, and the eye says which box is missing.
		const NO_BOX = { sph: null, cyl: null, axis: null, add: null, bc: null, dia: null };
		function convertEye(k) {
			const lens = $scope.lens(k);
			const out = Store.toContactRx($scope.spectacleRx(k), lens);
			$scope.converted[k] = out.vertexed;
			delete out.vertexed;
			if (lens.boxes) {
				const box = Store.boxFor(lens, out);
				angular.extend(p.cl[k], box ? box.rx : { sph: out.sph, cyl: out.cyl, axis: out.axis, add: out.add, bc: null, dia: null });
				return;
			}
			if (out.bc === null || lens.bcs.indexOf(p.cl[k].bc) !== -1) delete out.bc;
			if (out.dia === null) delete out.dia;
			angular.extend(p.cl[k], out);
		}

		// Whether each eye's powers are what the refraction gives. Then the notice says so and the convert
		// button, with nothing to do, is hidden - as on the spectacle dialog. Powers changed by hand bring
		// it back, as the way to start again from the refraction.
		const sameValue = (a, b) => (isNum(a) || isNum(b) ? isNum(a) && isNum(b) && Number(a) === Number(b) : (a || null) === (b || null));
		$scope.refractionApplied = function () {
			if (!p.product || !$scope.vision.data) return false;
			return lensEyes().every(function (k) {
				const lens = $scope.lens(k);
				let out = p.product.custom ? Store.toCustomRx($scope.spectacleRx(k), p.product.design) : Store.toContactRx($scope.spectacleRx(k), lens);
				// A made-to-order lens leaves an eye with no refraction as it is.
				if (p.product.custom && out.sph === null) return true;
				// An inventory lens: the box the refraction converts to, or the powers alone where it has none.
				if (lens && lens.boxes) {
					const box = Store.boxFor(lens, out);
					if (box) out = box.rx;
				}
				return ["sph", "cyl", "axis", "add"].every((f) => sameValue(out[f], p.cl[k][f]));
			});
		};

		function startFromRefraction() {
			lensEyes().forEach(function (k) {
				const out = Store.toCustomRx($scope.spectacleRx(k), p.product.design);
				if (out.sph === null) return;
				$scope.converted[k] = out.vertexed;
				angular.extend(p.cl[k], { sph: out.sph, cyl: out.cyl, axis: out.axis, add: out.add });
			});
			p.cl.source = "Refraction - starting point";
		}
		// The powers came from the refraction, not yet through a diagnostic lens.
		$scope.fromRefractionOnly = () => $scope.isCustom() && p.cl.source === "Refraction - starting point";

		// The right eye onto the left, which then wears a lens - so a right eye with none has nothing to copy.
		$scope.copyRightToLeft = function () {
			if ($scope.locked || $scope.noLens("od")) return;
			p.cl.os = angular.extend(angular.copy(p.cl.od), { noLens: false });
			fitEyeToLens("os");
		};

		// One eye with no lens - the other can't be without one too.
		$scope.toggleNoLens = function (k) {
			if ($scope.locked) return;
			const e = p.cl[k];
			if (!e.noLens && p.cl[k === "od" ? "os" : "od"].noLens) return;
			e.noLens = !e.noLens;
			if (e.noLens) angular.extend(e, { sph: null, cyl: null, axis: null, add: null });
			else fitEyeToLens(k);
		};

		// The left eye's own lens, from the stock lenses offered. Picking the right eye's lens again
		// makes it one lens for both.
		$scope.stockChoices = () => Store.contactLensChoices().filter((c) => !c.custom);
		$scope.leftChoices = () => $scope.stockChoices().filter((c) => !p.product || c.id !== p.product.id);
		$scope.leftLens = { id: null };
		const syncLeftLens = () => ($scope.leftLens.id = p.productOs ? p.productOs.id : null);
		syncLeftLens();
		$scope.$watch(() => p.productOs && p.productOs.id, syncLeftLens);
		$scope.setLeftLens = function () {
			if ($scope.locked || $scope.isCustom()) return;
			const id = $scope.leftLens.id;
			const chosen = id && id !== p.product.id ? $scope.stockChoices().find((c) => c.id === id) : null;
			p.productOs = chosen ? angular.copy(chosen) : null;
			if (!$scope.noLens("os")) {
				if ($scope.vision.today && (!p.cl.source || p.cl.source === "Converted from refraction")) {
					convertEye("os");
				} else {
					fitEyeToLens("os");
				}
			}
		};

		// A lens from inventory: its type and replacement were guessed from its name, so they can be set.
		$scope.setLensType = function (lens, design) {
			if ($scope.locked || !lens || !lens.fromInventory || lens.design === design) return;
			lens.design = design;
			["od", "os"].filter((k) => $scope.lens(k) === lens).forEach(fitEyeToLens);
		};
		$scope.setReplacement = function (lens, modality) {
			if (!$scope.locked && lens && lens.fromInventory) lens.modality = modality;
		};
		$scope.stockDesigns = Store.CL_DESIGNS.filter((d) => ["spherical", "toric", "multifocal"].indexOf(d.key) !== -1);
		$scope.stockModalities = Store.CL_MODALITIES.filter((m) => m.key !== "custom");

		// Each lens's manufacturer and material must be on the prescription. Inventory has them for a lens
		// described there; an older inventory lens, or a catalogue lens lacking one, has them entered here.
		// Made-to-order lenses name their lab on step 1 and their type per eye.
		$scope.needsDetails = (lens) => !!lens && !lens.custom && (!lens.maker || !lens.material);
		const detailsMissing = (lens) => !!lens && !lens.custom && (!String(lens.maker || lens.brand || "").trim() || !String(lens.material || "").trim());
		$scope.detailsMissing = () => $scope.lensSlots().some((s) => detailsMissing(s.lens) && lensEyes().some((k) => $scope.lens(k) === s.lens));

		// Custom lenses: the order power is the diagnostic lens plus the over-refraction through it.
		$scope.trialPower = (eye) => Store.orderPowerFromTrial(p.cl[eye].trial && p.cl[eye].trial.sph, p.cl[eye].or);

		$scope.useTrial = function (eye) {
			if ($scope.locked || !$scope.isCustom() || $scope.noLens(eye)) return;
			const e = p.cl[eye];
			const power = $scope.trialPower(eye);
			if (power === null) return;
			e.sph = power;
			if (isNum(e.trial.bc)) e.bc = Number(e.trial.bc);
			if (isNum(e.trial.dia)) e.dia = Number(e.trial.dia);
			p.cl.source = "Diagnostic lens + over-refraction";
		};

		// Powers a lens is made in, for the sphere dropdown: 0.25 steps to ±6, then 0.5. A power it isn't
		// made in - from an earlier prescription - is listed too, so the box shows it, and is flagged.
		const powerCache = {};
		const madePowers = (c) => powerCache[c.id] || (powerCache[c.id] = Store.clPowers(c.range));
		const powerMade = (k) => madePowers($scope.lens(k)).indexOf(Number(p.cl[k].sph)) !== -1;
		$scope.powers = function (eye) {
			const c = $scope.lens(eye);
			if (!c) return [];
			const sph = p.cl[eye].sph;
			if (!isNum(sph) || powerMade(eye)) return madePowers(c);
			// The same list while the power is the same, so the dropdown settles.
			const key = `${c.id} ${sph}`;
			return powerCache[key] || (powerCache[key] = madePowers(c).concat([Number(sph)]).sort((a, b) => b - a));
		};
		$scope.axes = Array.from({ length: 18 }, (x, i) => (i + 1) * 10);

		const between = (v, r) => isNum(v) && v >= r[0] && v <= r[1];

		// What a lens from inventory takes - wide, since the lens's own ranges aren't known: power in
		// 0.25 steps, a soft lens's base curve and diameter, minus cylinder.
		const STOCK_LIMITS = { sph: [-20, 20], bc: [7, 10], dia: [12, 16], cyl: [-10, -0.25] };
		const quarter = (v) => isNum(v) && Math.abs(Number(v) * 4 - Math.round(Number(v) * 4)) < 1e-9;
		function stockEyeError(k, field) {
			const e = p.cl[k];
			switch (field) {
				case "sph": return !(between(Number(e.sph), STOCK_LIMITS.sph) && quarter(e.sph));
				case "bc": return !between(Number(e.bc), STOCK_LIMITS.bc);
				case "dia": return !between(Number(e.dia), STOCK_LIMITS.dia);
				case "cyl": return $scope.isToric(k) && !(between(Number(e.cyl), STOCK_LIMITS.cyl) && quarter(e.cyl));
				case "axis": return $scope.isToric(k) && !(e.axis >= 1 && e.axis <= 180);
				case "add": return $scope.isMultifocal(k) && (!e.add || !!Store.addProblem(e.add));
			}
			return false;
		}

		$scope.eyeError = function (eye, field) {
			const e = p.cl[eye];
			const c = $scope.lens(eye);
			if (!c || e.noLens) return false;
			const touched = !!$scope.visited.issue;
			// No box chosen yet is only an error once the user has moved on; powers that aren't a box are one now.
			if (c.boxes) return field === "box" && !Store.stockFor(c, e) && (touched || isNum(e.sph));
			if (c.fromInventory) {
				const blank = ["sph", "bc", "dia"].indexOf(field) !== -1 && !isNum(e[field]);
				return blank ? touched : stockEyeError(eye, field);
			}
			if (c.custom) {
				switch (field) {
					case "sph": return isNum(e.sph) ? !between(e.sph, c.range) : touched;
					case "bc": return isNum(e.bc) ? !between(e.bc, c.bcRange) : touched;
					case "dia": return isNum(e.dia) ? !between(e.dia, c.diaRange) : touched;
					case "material": return !String(e.material || "").trim() && touched;
					case "bcSteep": return !!steepBcProblem(e, c);
					case "orAxis": return !!orAxisProblem(e);
					case "cyl": return $scope.isToric(eye) && !(Number(e.cyl) < 0);
					case "axis": return $scope.isToric(eye) && !(e.axis >= 1 && e.axis <= 180);
					case "add": return $scope.isMultifocal(eye) && (!(Number(e.add) > 0) || !!Store.addProblem(e.add));
				}
				return false;
			}
			switch (field) {
				case "sph": return !isNum(e.sph) ? touched : !powerMade(eye);
				case "cyl": return $scope.isToric(eye) && !isNum(e.cyl);
				case "axis": return $scope.isToric(eye) && !(e.axis >= 10 && e.axis <= 180);
				case "add": return $scope.isMultifocal(eye) && !e.add;
				case "bc": return !e.bc;
			}
			return false;
		};

		// A back-toric hard lens's steep base curve: optional (a front toric has one base curve), but when
		// given it is in the lens's range and steeper - smaller - than the flat one.
		function steepBcProblem(e, c) {
			if (!isNum(e.bcSteep)) return null;
			if (!between(Number(e.bcSteep), c.bcRange)) return "range";
			return isNum(e.bc) && !(Number(e.bcSteep) < Number(e.bc)) ? "flatter" : null;
		}
		// A cylinder in the over-refraction through the diagnostic lens needs its axis.
		const orAxisProblem = (e) => (isNum(e.orCyl) && Number(e.orCyl) !== 0 && !(Number(e.orAxis) >= 1 && Number(e.orAxis) <= 180) ? "axis" : null);

		function eyeValid(k) {
			const e = p.cl[k];
			const c = $scope.lens(k);
			if (!c) return false;
			if (c.boxes) return !!Store.stockFor(c, e);
			if (c.fromInventory) return ["sph", "bc", "dia", "cyl", "axis", "add"].every((f) => !stockEyeError(k, f));
			if (c.custom) {
				if (!between(e.sph, c.range) || !between(e.bc, c.bcRange) || !between(e.dia, c.diaRange) || !String(e.material || "").trim()) return false;
				if ($scope.isToric(k) && !(Number(e.cyl) < 0 && e.axis >= 1 && e.axis <= 180)) return false;
				if (steepBcProblem(e, c) || orAxisProblem(e)) return false;
				if ($scope.isMultifocal(k) && (!(Number(e.add) > 0) || Store.addProblem(e.add))) return false;
				return true;
			}
			if (!isNum(e.sph) || !powerMade(k) || !e.bc) return false;
			if ($scope.isToric(k) && (!isNum(e.cyl) || !(e.axis >= 10 && e.axis <= 180))) return false;
			if ($scope.isMultifocal(k) && !e.add) return false;
			return true;
		}

		// Powers for each eye that wears a lens - at least one does.
		$scope.clValid = () => !!p.product && lensEyes().length > 0 && lensEyes().every(eyeValid);

		// What is still needed, said under the table.
		$scope.clHint = function () {
			const c = p.product;
			if (!c) return "";
			if ($scope.isCustom()) {
				return `Each eye that wears a lens needs a base curve (${$filter("number")(c.bcRange[0], 2)}-${$filter("number")(c.bcRange[1], 2)} mm), power (${Store.fmtPower(c.range[0])} to ${Store.fmtPower(c.range[1])}), diameter (${$filter("number")(c.diaRange[0], 1)}-${$filter("number")(c.diaRange[1], 1)} mm) and material${$scope.isToric() ? ", plus cylinder and axis - and a steep base curve, steeper than the flat one, for a back toric" : ""}${$scope.isMultifocal() ? ", plus the add" : ""}. An over-refraction with cylinder needs its axis.`;
			}
			if (lensEyes().some((k) => $scope.hasBoxes(k))) {
				return "Choose a box for each eye that wears a lens: a prescription the practice has in inventory, in stock or to order in. To prescribe one it hasn't, add it to the lens in Inventory first.";
			}
			const stock = lensEyes().some((k) => $scope.isFromInventory(k));
			const needs = stock
				? "a power within its lens's range (in 0.25 steps), base curve (7.0-10.0) and diameter (12.0-16.0)"
				: "a power its lens is made in (0.25 steps to ±6.00, then 0.50) and a base curve";
			return `Each eye that wears a lens needs ${needs}, plus cylinder and axis for a toric lens and the add for a multifocal.`;
		};

		// A trial that hasn't settled - adjust or refit - isn't a prescription yet.
		function fitSettled() {
			return !p.fitting.trialIssued || p.fitting.outcome === "good";
		}

		$scope.fmtEye = function (e) {
			if (e && e.noLens) return "No lens";
			if (!e || !isNum(e.sph)) return "-";
			let s = Store.fmtPower(e.sph);
			if (isNum(e.cyl) && e.cyl !== 0) s += ` / ${Store.fmtPower(e.cyl)} × ${e.axis || "?"}`;
			if (e.add) s += `  Add ${typeof e.add === "number" ? Store.fmtPower(e.add) : e.add}`;
			return s;
		};
		$scope.fmtFit = function (e) {
			if (!e || e.noLens || !e.bc) return "";
			if (!$scope.isCustom()) return `BC ${Number(e.bc).toFixed(1)} · DIA ${Number(e.dia).toFixed(1)}`;
			return `BC ${Number(e.bc).toFixed(2)}${e.bcSteep ? " / " + Number(e.bcSteep).toFixed(2) : ""} · DIA ${Number(e.dia).toFixed(1)}${e.material ? " · " + e.material : ""}${e.colour && e.colour !== "Clear" ? " " + e.colour.toLowerCase() : ""}`;
		};
		$scope.lensLine = (lens) => (lens ? `${lens.brand || ""} ${lens.name || ""}`.trim() : "");

		$scope.wearSchedules = [
			{ key: "daily", label: "Daily wear" },
			{ key: "part", label: "Part-time / sport" },
			{ key: "extended", label: "Extended (overnight)" },
			{ key: "overnight", label: "Overnight (ortho-K)" },
		];

		// An ortho-K lens is worn overnight and taken out on waking: choosing one sets the schedule, and a
		// lens that isn't ortho-K doesn't keep it. The patient's copy prints how to wear them.
		$scope.isOrthoK = () => ["od", "os"].some((k) => !$scope.noLens(k) && $scope.lens(k) && $scope.lens(k).design === "orthok");
		// The schedules offered: overnight only for ortho-K. Two fixed lists, so the template's repeat settles.
		const everydayWear = $scope.wearSchedules.filter((w) => w.key !== "overnight");
		$scope.wearChoices = () => ($scope.isOrthoK() || p.fitting.wear === "overnight" ? $scope.wearSchedules : everydayWear);
		$scope.$watch(() => !!p.product && $scope.isOrthoK(), function (orthoK, before) {
			if ($scope.locked) return;
			if (orthoK) p.fitting.wear = "overnight";
			else if (before && p.fitting.wear === "overnight") p.fitting.wear = "daily";
		});
		$scope.wearLabel = (key) => ($scope.wearSchedules.find((w) => w.key === key) || {}).label || "-";

		// The trial, in its own dialog. The draft is saved first - the trial is kept with it - and this dialog
		// closes; the prescriptions tile opens the trial, and opens this again from it.
		$scope.onTrial = () => Rx.onTrial(p);
		function toTrial(newTrial) {
			if ($scope.locked || $scope.saving) return;
			$scope.saving = true;
			Rx.save(patient.id, p)
				.then((saved) => $mdDialog.hide({ next: "trial", prescription: saved, newTrial: newTrial }))
				.catch((resp) => failMessage(resp, "The draft could not be saved."))
				.finally(() => ($scope.saving = false));
		}
		// A trial pair is of the lenses written here, so they need their powers first. After a review that
		// asked for a change, the new pair starts the trial again.
		$scope.startTrial = () => $scope.clValid() && toTrial(true);
		$scope.openTrial = () => toTrial(false);

		$scope.setFitting = function (key, value) {
			if ($scope.locked) return;
			p.fitting[key] = value;
		};

		// Start from an earlier contact lens prescription's lens and powers, from the history.
		$scope.useEarlier = function (earlier) {
			if ($scope.locked) return;
			Rx.copyContactRx(p, angular.extend({}, earlier, { product: asOfferedNow(earlier.product), productOs: asOfferedNow(earlier.productOs) }));
		};

		// The last contact lens prescription, what has changed since, and anything worth a second look.
		$scope.previous = () => Rx.previousOf(patient.id, p);
		$scope.changes = function () {
			const prev = $scope.previous();
			return prev && $scope.clValid() ? Rx.changesFrom(prev, p) : null;
		};
		// A boolean for ng-if: changes() is a new object on every digest.
		$scope.hasChanges = () => !!$scope.changes();
		$scope.warnings = () => ($scope.clValid() ? Rx.rxWarnings(p, $scope.previous()) : []);

		// ------------------------------------------------------------------
		// Step 3 - review & issue
		// ------------------------------------------------------------------
		const today = () => {
			const d = new Date();
			d.setHours(0, 0, 0, 0);
			return d;
		};

		// What stops it being issued, each with the step that fixes it - the same list while nothing changes,
		// as for the lens slots.
		let missingKept = { json: null, list: [] };
		$scope.missing = function () {
			const out = missingNow();
			const json = angular.toJson(out);
			if (json !== missingKept.json) missingKept = { json: json, list: out };
			return missingKept.list;
		};
		function missingNow() {
			const out = [];
			if (!p.product) out.push({ label: "Contact lens", step: 0 });
			else if (!$scope.lensChosen()) out.push({ label: "The made-to-order lens's name and lab", step: 0 });
			else if (!$scope.clValid()) out.push({ label: "Lens powers", step: FITTING });
			if (p.product && $scope.detailsMissing()) out.push({ label: "Each lens's manufacturer and material", step: FITTING });
			if (!fitSettled()) out.push({ label: "The trial's outcome", trial: true });
			if ($scope.isOrthoK() && p.fitting.wear !== "overnight") out.push({ label: "The overnight wearing schedule for ortho-K", step: FITTING });
			if (!p.cl.expires) out.push({ label: "CL Rx expiry date", step: FITTING });
			else if (new Date(p.cl.expires) < today()) out.push({ label: "An expiry date that hasn't passed", step: FITTING });
			return out;
		}
		$scope.canIssue = () => !$scope.missing().length && !$scope.issuing && !$scope.locked;

		$scope.issuing = false;
		$scope.saving = false;

		const failMessage = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		$scope.issue = function () {
			if (!$scope.canIssue()) return;
			$scope.issuing = true;
			Rx.issue(patient.id, p, null)
				// Issued is the end of the dialog's work: it closes, as a saved draft does. The server files the
				// patient's copy in their documents; the prescription is issued whether or not that works.
				.then(function (issued) {
					if (!Rx.copyFiled(issued)) copyNotSaved();
					$mdDialog.hide(issued);
				})
				.catch((resp) => failMessage(resp, "The prescription could not be issued."))
				.finally(() => ($scope.issuing = false));
		};

		const copyNotSaved = () =>
			toast.create({ timeout: 8000, message: "The prescription was issued, but its copy couldn't be saved to the patient's documents. Open it from Prescriptions to print it.", className: "alert-warning", dismissible: true });

		$scope.saveDraft = function () {
			if ($scope.saving) return;
			$scope.saving = true;
			Rx.save(patient.id, p)
				.then((saved) => $mdDialog.hide(saved))
				.catch((resp) => failMessage(resp, "The draft could not be saved."))
				.finally(() => ($scope.saving = false));
		};

		$scope.$on("$destroy", () => $timeout.cancel(advanceTimer));

		// ------------------------------------------------------------------
		// Close
		// ------------------------------------------------------------------
		$scope.confirmClose = false;
		// See the spectacle dialog: set from here, not from inside the prompt's ng-if child scope.
		$scope.keepEditing = () => ($scope.confirmClose = false);
		$scope.close = function () {
			if (($scope.dirty || isRenewal) && !$scope.locked && p.product) {
				$scope.confirmClose = true;
				return;
			}
			$mdDialog.cancel();
		};
		$scope.discard = () => $mdDialog.cancel();
	},
]);
